/**
 * Brand-name SSoT (D-2/D-6, 2026-07-29).
 *
 * Five forms of the company name were shipping side by side — the landing said
 * "Pelican Energy TCI", the app nav said "Pelican Energy", the mobile header said
 * "PETCI", the PWA installed as "Pelican TCI" — and the copy fixes alone would not
 * have stopped a sixth appearing, because nothing named the policy in one place.
 *
 * These strings are VERBATIM across every locale. They are proper nouns, so they
 * are deliberately NOT in the i18n catalogues: translating a company name is a
 * defect, not a feature. Anything that reads AROUND the name (aria labels,
 * sentences that mention it) stays keyed as normal — see `brand.homeAria`.
 *
 * Lives in `@petci/shared-ui` rather than `@petci/branding` because both apps
 * already import this barrel everywhere, while no mobile source imports
 * `@petci/branding` at all — four strings is not the place to find out whether
 * Metro can resolve that package. Pure strings, no side effects, so a Pages
 * Function that deep-imports from here pays nothing.
 */
export const BRAND = {
  /**
   * Identity, legal, first mention, install names — AND every wordmark with the
   * measured width to carry it, including the signed-in web chrome (R-N4,
   * 2026-07-30). The default choice, and now the default in a stronger sense: the
   * policy is FULL FORM WHEREVER WIDTH ALLOWS, not "form follows surface class".
   *
   * The old policy gave the landing "Pelican Energy TCI" and the app "Pelican
   * Energy" on the reasoning that the customer had already been introduced. That
   * reasoning is real but it is taste; against it, the split reads as drift to
   * anyone comparing the two surfaces, "TCI" is the islands identity this product
   * puts first, and selecting by measured width is ONE rule where selecting by
   * voice was two. Godfred challenged the split and ratified this in-session.
   */
  full: "Pelican Energy TCI",
  /**
   * WIDTH-CONSTRAINED CHROME ONLY — today that means the mobile header band,
   * where the arithmetic genuinely forbids the full form: it cannot sit beside
   * four 48dp persona chips on a phone (see AppHeader's WORDMARK_MIN_WIDTH for
   * the measurement). This is physics, not voice, which is why mobile keeps its
   * ladder while web converged on `full`.
   *
   * Never in prose: a sentence that says "Pelican Energy" reads as a different
   * company from the one on the landing page.
   */
  short: "Pelican Energy",
  /**
   * The narrowest tiers, where even `short` will not fit: the narrow mobile
   * header, and web below `sm:` (R-N4 — web rendered NO wordmark at all there
   * until 2026-07-30, which is the same wordless-brand defect D-4 fixed on
   * mobile; the width picks WHICH form renders, never WHETHER one does).
   *
   * D-3: replaces the compact "PETCI" wordmark. The acronym is an internal
   * initialism that no public surface ever expands, so the first place a customer
   * met it was a biometric prompt saying "Sign in to PETCI". "Pelican" is
   * recognisable on its own; the acronym is not.
   */
  compact: "Pelican",
  /** PWA `short_name` (documented here for the policy; not consumed at runtime). */
  pwaShort: "Pelican TCI",
} as const;
